import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { useSeo, SEO_KEYWORDS } from '../hooks/useSeo';

const TermsOfServicePage = () => {
  useSeo({ title: 'Terms of Service | InvestiGate', description: 'Read the InvestiGate terms of service for our investigation and case management platform.', keywords: SEO_KEYWORDS });
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center space-x-4">
          <Link to="/" className="text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <Shield className="w-8 h-8 text-blue-600" />
          <span className="text-2xl font-bold text-gray-900">InvestiGate</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
          <p className="text-gray-600 mb-8">
            <strong>Effective Date:</strong> June 23, 2025
          </p>

          <div className="prose prose-blue max-w-none">
            <p className="text-lg text-gray-700 mb-8">
              Welcome! By signing up for a membership, you agree to the following terms:
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-blue-600 mr-3">1️⃣</span> Services Provided
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Your membership grants access to investigative tools, research guides, case management features, evidence storage, AI-powered document analysis, team collaboration tools, and all other features included in your subscription tier.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-blue-600 mr-3">2️⃣</span> Payments & Refunds
              </h2>
              <p className="text-gray-700 leading-relaxed">
                All sales are final. <strong>No refunds will be issued</strong> once a membership payment has been processed. Please review your subscription choice carefully before completing your purchase.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-blue-600 mr-3">3️⃣</span> Cancellation
              </h2>
              <p className="text-gray-700 leading-relaxed">
                You may cancel your membership at any time through your account settings. Access will continue until the end of your paid period. No partial refunds will be provided for unused time within a billing cycle.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-blue-600 mr-3">4️⃣</span> Account Use & Restrictions
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree not to share your account login with others. A maximum of two simultaneous device logins is allowed per member. 
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Violating this may result in suspension or termination of your account</strong> without refund. You are responsible for maintaining the security of your account credentials.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-blue-600 mr-3">5️⃣</span> Privacy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Your personal information (such as name and email) is stored securely on the membership platform and is <strong>never sold or shared with third parties</strong>. We use industry-standard encryption and security measures to protect your data. For more details, see our <Link to="/privacy" className="text-blue-600 hover:text-blue-700 underline">Privacy Policy</Link>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-blue-600 mr-3">6️⃣</span> Disclaimer of Responsibility
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                InvestiGate provides tools and information for <strong>educational and research purposes only</strong>. 
              </p>
              <p className="text-gray-700 leading-relaxed">
                We are not responsible for how members use the investigative tools, nor for any consequences arising from misuse or illegal use. Members must comply with all applicable laws and regulations in their jurisdiction when using our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-blue-600 mr-3">7️⃣</span> Changes to Terms
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to update these terms at any time. Members will be notified of significant changes via email. Continued use of the service after changes constitute acceptance of the new terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-blue-600 mr-3">8️⃣</span> Acceptable Use
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You agree to use InvestiGate only for lawful purposes. You may not:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Use the service for any illegal activities</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Upload malicious code or viruses</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Impersonate others or provide false information</li>
                <li>Scrape or copy content without permission</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-blue-600 mr-3">9️⃣</span> Intellectual Property
              </h2>
              <p className="text-gray-700 leading-relaxed">
                All content, features, and functionality of InvestiGate are owned by us and are protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-blue-600 mr-3">🔟</span> Limitation of Liability
              </h2>
              <p className="text-gray-700 leading-relaxed">
                InvestiGate is provided "as is" without warranties of any kind. We are not liable for any indirect, incidental, special, or consequential damages arising from your use of the service. Our total liability shall not exceed the amount you paid for your subscription in the past 12 months.
              </p>
            </section>

            <section className="mb-8 border-2 border-red-200 bg-red-50 p-6 rounded-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="text-red-600 mr-3">⚖️</span> Binding Arbitration & Class Action Waiver
              </h2>
              
              <div className="space-y-4 text-gray-700">
                <p className="font-semibold text-red-900">
                  PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT.
                </p>

                <h3 className="text-lg font-bold text-gray-900 mt-4">Agreement to Arbitrate</h3>
                <p className="leading-relaxed">
                  You and InvestiGate agree that any dispute, claim, or controversy arising out of or relating to these Terms of Service or your use of the InvestiGate platform (collectively, "<strong>Disputes</strong>") will be settled by <strong>binding arbitration</strong>, except that each party retains the right to seek injunctive or other equitable relief in a court of competent jurisdiction to prevent the actual or threatened infringement, misappropriation, or violation of a party's copyrights, trademarks, trade secrets, patents, or other intellectual property rights.
                </p>

                <h3 className="text-lg font-bold text-gray-900 mt-4">Class Action Waiver</h3>
                <p className="leading-relaxed">
                  <strong>You and InvestiGate agree that each party may bring Disputes against the other party only in an individual capacity, and not as a plaintiff or class member in any purported class, consolidated, or representative action or proceeding.</strong> Unless both you and InvestiGate agree otherwise, the arbitrator may not consolidate or join more than one person's or party's claims and may not otherwise preside over any form of a consolidated, representative, or class proceeding.
                </p>

                <h3 className="text-lg font-bold text-gray-900 mt-4">Arbitration Procedures</h3>
                <p className="leading-relaxed mb-3">
                  The arbitration will be conducted by the <strong>American Arbitration Association (AAA)</strong> under its Commercial Arbitration Rules and, where appropriate, the AAA's Supplementary Procedures for Consumer Related Disputes. The AAA's rules are available at <a href="https://www.adr.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-700">www.adr.org</a>.
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>The arbitration will be held in the United States, in the county where you reside or another mutually agreed location</li>
                  <li>Payment of all filing, administration, and arbitrator fees will be governed by the AAA's rules</li>
                  <li>The arbitration may be conducted in person, through document submission, by phone, or online</li>
                  <li>The arbitrator will have exclusive authority to resolve all disputes, including threshold matters of arbitrability</li>
                  <li>The arbitrator's decision and award will be in writing and binding on both parties</li>
                  <li>Judgment on the arbitrator's award may be entered in any court of competent jurisdiction</li>
                </ul>

                <h3 className="text-lg font-bold text-gray-900 mt-4">Exceptions to Arbitration</h3>
                <p className="leading-relaxed mb-3">
                  Either party may seek resolution in small claims court for disputes or claims within the scope of that court's jurisdiction. Additionally, disputes concerning patents, copyrights, moral rights, trademarks, and trade secrets and claims of piracy or unauthorized use of the InvestiGate platform will not be subject to arbitration.
                </p>

                <h3 className="text-lg font-bold text-gray-900 mt-4">30-Day Opt-Out Period</h3>
                <p className="leading-relaxed">
                  You have the right to opt out of this arbitration agreement within <strong>30 days</strong> of first accepting these Terms. To opt out, you must send written notice to <a href="mailto:legal@investigate.com" className="text-blue-600 underline hover:text-blue-700">legal@investigate.com</a> with your name, address, email, and a clear statement that you wish to opt out of this arbitration agreement. If you opt out, you and InvestiGate will not be bound by this arbitration agreement, but all other parts of these Terms will continue to apply.
                </p>

                <h3 className="text-lg font-bold text-gray-900 mt-4">Severability</h3>
                <p className="leading-relaxed">
                  If any portion of this arbitration agreement is found to be unenforceable or unlawful for any reason, the unenforceable or unlawful provision shall be severed from these Terms. Severance of the unenforceable or unlawful provision shall have no impact whatsoever on the remainder of the arbitration agreement or the parties' ability to compel arbitration of any remaining claims on an individual basis.
                </p>

                <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                  <p className="text-sm font-semibold text-yellow-900 mb-2">
                    ⚠️ Important Legal Notice
                  </p>
                  <p className="text-sm text-yellow-800">
                    By accepting these Terms of Service, you are agreeing to resolve disputes through binding arbitration instead of in court. You are also waiving your right to participate in class action lawsuits. If you do not agree to these terms, you must opt out within 30 days.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8 bg-blue-50 border-l-4 border-blue-600 p-6 rounded">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-2">
                If you have questions about these terms, please contact us:
              </p>
              <p className="text-gray-700">
                <strong>Email:</strong> <a href="mailto:support@investigate.com" className="text-blue-600 hover:text-blue-700 underline">support@investigate.com</a>
              </p>
            </section>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-4">
                <strong>Last Updated:</strong> June 23, 2025
              </p>
              <p className="text-sm text-gray-600">
                By using InvestiGate, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </div>
          </div>
        </div>
      </main>

      <footer className="bg-gray-900 text-gray-400 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center space-x-6 mb-4">
            <Link to="/terms" className="hover:text-white transition">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link>
            <Link to="/" className="hover:text-white transition">Home</Link>
          </div>
          <p>&copy; 2025 InvestiGate. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
